import Store from 'repatch';

import { GraphQLOperationFailed } from '../adapter/GraphQLClient';
import State from '../core/State';
import { demoCreationData } from '../data/demo_project/creation';
import { demoCreationSetData } from '../data/demo_project/creationSet';
import { CreationDetailFragment, File } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import FileHandler from '../service/FileHandler';
import CreationSetWebApi from '../service/persistent/CreationSetWebApi';
import CreationWebApi from '../service/persistent/CreationWebApi';
import { disposeCreation, finishFetchingCreation, startFetchingCreation } from '../thunk/creation';
import {
  disposeCreationSetWithFetching,
  finishFetchingCreationSet,
  startFetchingCreationSet,
} from '../thunk/creationSet';
import { setUploadedAdPlatformKeysMap } from '../thunk/creativeSending';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import { disposeProjectAndCreations, startFetchingProject } from '../thunk/project';

class ShowCreationUsecase {
  private store: Store<State>;
  private fileHandler: FileHandler;
  private creationWebApi: CreationWebApi;
  private creationSetWebApi: CreationSetWebApi;
  private errorPresenter: ErrorPresenter;

  constructor({
    store,
    fileHandler,
    creationWebApi,
    creationSetWebApi,
    errorPresenter,
  }: {
    store: Store<State>;
    fileHandler: FileHandler;
    creationWebApi: CreationWebApi;
    creationSetWebApi: CreationSetWebApi;
    errorPresenter: ErrorPresenter;
  }) {
    this.store = store;
    this.fileHandler = fileHandler;
    this.creationWebApi = creationWebApi;
    this.creationSetWebApi = creationSetWebApi;
    this.errorPresenter = errorPresenter;
  }

  public downloadLatestDeliverableCreative = (deliverable: {
    creative: {
      file: Pick<File, 'id' | 'url' | 'mimeType'>;
    };
  }): void => {
    this.fileHandler.downloadFile(deliverable.creative.file);
  };

  public downloadAttachedThumbnail = (deliverable: {
    attachedThumbnail?: {
      file: Pick<File, 'id' | 'url' | 'mimeType'>;
    } | null;
  }): void => {
    if (!deliverable.attachedThumbnail) return;
    this.fileHandler.downloadFile(deliverable.attachedThumbnail.file);
  };

  public prepareCreation = async (creationId: string): Promise<void> => {
    const demoCreation = demoCreationData.find((v) => v.data.creation.id === creationId);
    if (demoCreation) {
      this.prepareDemoCreation(creationId);
      return;
    }

    this.store.dispatch(startFetchingCreation());
    this.store.dispatch(startFetchingCreationSet());
    this.store.dispatch(startFetchingProject());

    let creation: CreationDetailFragment;
    try {
      creation = await this.creationWebApi.getByIdWithoutPricingData(creationId);
    } catch (e) {
      if (e instanceof GraphQLOperationFailed) {
        if (e.type === 'UnauthorizedError') {
          console.error(e.type);
          this.errorPresenter.showAsFatal(new Error('duplicate_business_account_error'));
          return;
        }
      }
      throw e;
    }
    const [creationSet] = await Promise.all([this.creationSetWebApi.getByIdWithoutPricingData(creation.creationSetId)]);

    const uploadedAdPlatformKeysMap = new Map<string, Array<string>>();
    creation.deliverables.forEach((deliverable) => {
      uploadedAdPlatformKeysMap.set(deliverable.id, deliverable.uploadedAdPlatformKeys || []);
    });

    this.store.dispatch(setUploadedAdPlatformKeysMap(uploadedAdPlatformKeysMap));

    if (creation.organization) {
      this.store.dispatch(setLastAccessedOrganization(creation.organization));
    }
    this.store.dispatch(finishFetchingCreationSet(creationSet));
    this.store.dispatch(finishFetchingCreation(creation));
  };

  public prepareDemoCreation = async (creationId: string): Promise<void> => {
    const demoCreation = demoCreationData.find((v) => v.data.creation.id === creationId)!;
    const demoCreationSet = demoCreationSetData.find((v) =>
      v.data.creationSet.creations.some((v) => v.id === demoCreation.data.creation.id),
    )!;

    this.store.dispatch(startFetchingCreation());
    this.store.dispatch(startFetchingCreationSet());
    this.store.dispatch(startFetchingProject());

    this.store.dispatch(finishFetchingCreationSet(demoCreationSet.data.creationSet));
    this.store.dispatch(finishFetchingCreation(demoCreation.data.creation as CreationDetailFragment));
  };

  public disposeCreation = (): void => {
    this.store.dispatch(disposeCreation());
    this.store.dispatch(disposeCreationSetWithFetching());
    this.store.dispatch(disposeProjectAndCreations());
  };
}

export default ShowCreationUsecase;
